import { ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';

import { AllLabels, FacilityFormType, GridConnectionApplicationType, InstallationFlowType } from '../../../constants/app.constants';
import { FormTypesData, KeyValueData } from '../../../domains/common.domain';
import { TechnicianDataService } from '../technician-data.service';
import { LocalStorageService } from '../../../services/local-storage.service';
import { EnvironmentService } from '../../../services/environment.service';
import { ValidationsService } from '../../../services/validations.service';

@Component({
  selector: 'app-gas-connection',
  styleUrls: ['./gas-connection.component.scss'],
  templateUrl: './gas-connection.component.html',
})
export class TechnicianGasConnectionComponent implements OnChanges, OnInit {
  @Input() currentFormType: FormTypesData;
  @Input() gasConnectionData: any = null;
  @Input() isReadOnly: boolean = false;

  currentFlowType: string;
  formSubscriptions: Subscription[] = [];
  gasConnectionFG: FormGroup;
  handoverLocationPowerRequired: boolean;
  infoBubblesData: {
    [key: string]: boolean;
  };
  installationFlowTypes: KeyValueData;
  showConnectedLoadLabel: boolean;
  showUsageSection: boolean;
  showInfoForCommodities: boolean;
  showDisposalOfOilTankRadio: boolean;
  unitLabels: KeyValueData;

  constructor(
    private environmentService: EnvironmentService,
    private localStorageService: LocalStorageService,
    private technicianDataService: TechnicianDataService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.handoverLocationPowerRequired = this.environmentService.handoverLocationPowerRequired;
    this.showUsageSection = this.environmentService.showUsageSection;
    this.showInfoForCommodities = this.environmentService.showInfoForCommodities;
    this.showConnectedLoadLabel = this.environmentService.showConnectedLoadLabel;
    this.showDisposalOfOilTankRadio = this.environmentService.showDisposalOfOilTankRadio;
    this.unitLabels = AllLabels.units;
    this.installationFlowTypes = InstallationFlowType;
    this.currentFlowType = this.localStorageService.getItem('installation_flow');
    this.infoBubblesData = this.environmentService.getInfoBubblesData('technicianData') || null;
    this.prepareForm();
    this.technicianDataService.registerForActionOnFormSubmit.subscribe((value: boolean) => {
      if (value) {
        this.emitFormData();
        this.resetData();
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      (changes && changes.currentFormType && !changes.currentFormType.firstChange) ||
      (changes && changes.baseData && !changes.baseData.firstChange)
    ) {
      this.prepareForm();
    }
  }

  isFieldRequired(field: string): boolean {
    const formControl: AbstractControl = this.gasConnectionFG.get(field);
    return ValidationsService.isFieldRequired(formControl);
  }

  private prepareForm(): void {
    let handoverLocationPowerDefaultValue =
      this.currentFlowType === this.installationFlowTypes.MainsConnection
        ? this.environmentService.handoverLocationPowerDefaultValueForMainsConnection
        : this.environmentService.handoverLocationPowerDefaultValue;
    if (this.currentFlowType === this.installationFlowTypes.ConversionToGas) {
      handoverLocationPowerDefaultValue =
        this.environmentService.handoverLocationPowerDefaultValueForConversionOfGas?.toString() ?? handoverLocationPowerDefaultValue;
    }
    this.gasConnectionFG = new FormGroup({
      bhkwCheckbox: new FormControl((this.gasConnectionData && this.gasConnectionData.bhkwCheckbox) || false, [Validators.required]),
      cookingGasCheckbox: new FormControl((this.gasConnectionData && this.gasConnectionData.cookingGasCheckbox) || false, [
        Validators.required,
      ]),
      heatingGasCheckbox: new FormControl((this.gasConnectionData && this.gasConnectionData.heatingGasCheckbox) || false, [
        Validators.required,
      ]),
      handoverLocationPower: new FormControl(
        (this.gasConnectionData && this.gasConnectionData.handoverLocationPower) || handoverLocationPowerDefaultValue
      ),
      id: new FormControl((this.gasConnectionData && this.gasConnectionData.id) || ''),
    });

    if (this.currentFormType.index > 0) {
      this.gasConnectionFG.addControl(
        'processGasCheckbox',
        new FormControl((this.gasConnectionData && this.gasConnectionData.processGasCheckbox) || false, [Validators.required])
      );
    }

    if (
      this.currentFlowType === this.installationFlowTypes.MainsConnection ||
      this.currentFlowType === this.installationFlowTypes.ConversionToGas
    ) {
      if (this.handoverLocationPowerRequired) {
        this.gasConnectionFG.controls.handoverLocationPower.setValidators(Validators.required);
        this.gasConnectionFG.controls.handoverLocationPower.updateValueAndValidity();
      }
    }

    if (
      this.currentFlowType === this.installationFlowTypes.ChangeHouseConnection &&
      this.environmentService.handoverLocationPowerRequiredForChangeHouseConnection
    ) {
      this.gasConnectionFG.controls.handoverLocationPower.setValidators(Validators.required);
      this.gasConnectionFG.controls.handoverLocationPower.updateValueAndValidity();
    }

    if (
      this.showDisposalOfOilTankRadio &&
      (this.currentFlowType === this.installationFlowTypes.MainsConnection ||
        (this.currentFlowType === this.installationFlowTypes.ConversionToGas &&
          this.currentFormType.key === GridConnectionApplicationType.ConversionGas))
    ) {
      this.gasConnectionFG.addControl(
        'disposalOfOilTank',
        new FormControl((this.gasConnectionData && this.gasConnectionData.disposalOfOilTank) || false, [Validators.required])
      );
      if (this.currentFlowType === this.installationFlowTypes.MainsConnection) {
        this.gasConnectionFG.addControl(
          'sizeOfTank',
          new FormControl((this.gasConnectionData && this.gasConnectionData.sizeOfTank) || '', [])
        );
        this.gasConnectionFG.addControl(
          'boilerOutputHeatingGas',
          new FormControl((this.gasConnectionData && this.gasConnectionData.boilerOutputHeatingGas) || '', [Validators.required])
        );
        this.gasConnectionFG.addControl(
          'waterTreatment',
          new FormControl((this.gasConnectionData && this.gasConnectionData.waterTreatment) || false, [Validators.required])
        );
        this.gasConnectionFG.addControl(
          'waterProtectionArea',
          new FormControl((this.gasConnectionData && this.gasConnectionData.waterProtectionArea) || false, [Validators.required])
        );
      }
    }

    // if (this.currentFormType.index === 2 ||
    // this.currentFlowType === this.installationFlowTypes.ConversionToGas) {
    //   this.gasConnectionFG.addControl('desiredTransferPressure',
    //     new FormControl(
    //       {
    //         value: this.gasConnectionData && this.gasConnectionData.desiredTransferPressure || '',
    //         disabled: this.isReadOnly,
    //       }));
    // }
    this.emitFormData();

    this.formSubscriptions.push(
      this.gasConnectionFG.valueChanges.subscribe(() => {
        this.emitFormData();
      })
    );
    this.formSubscriptions.push(
      this.gasConnectionFG.get('disposalOfOilTank')?.valueChanges.subscribe(value => {
        this.gasConnectionFG.controls.sizeOfTank.setValidators(value ? [Validators.required] : []);
        this.gasConnectionFG.controls.sizeOfTank.updateValueAndValidity();
      })
    );
    this.cdr.detectChanges();
  }

  private emitFormData(): void {
    this.technicianDataService.registerForComponentFormUpdate.next({
      component: 'gasConnection',
      formData: this.gasConnectionFG,
    });
  }

  private resetData(): void {
    this.gasConnectionData = null;
    this.formSubscriptions.forEach(fs => fs?.unsubscribe());
  }
}
